import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import TableAddRow from "./TableAddRow.jsx";
import TableBody from "./TableBody.jsx";
import TableContainer from "./TableContainer.jsx";
import TableHeader from "./TableHeader.jsx";
import TableSearch from "./TableSearch.jsx";
import findNewRowKey from "../../../helpers/table/findNewRowKey.js";
import sortItems from "../../../helpers/table/sortItems.js";

// Alleen na de eigen klik op '+ toevoegen' schuift de pagina mee, en alleen
// als de nieuwe rij binnen deze tijd verschijnt (de write gaat via Firestore).
// Een rij die een collega toevoegt, of die terugkomt als je een zoekletter
// wist, laat de pagina niet verspringen terwijl iemand leest of typt.
const USER_ADD_WINDOW_MS = 10000;

// Een rij die onderaan wordt toegevoegd kan buiten beeld vallen. De tabel heeft
// sinds docs/features/scrollbalken-opruimen.md (T3) geen eigen hoogte meer, dus
// meestal moet de PAGINA meeschuiven; bij een tabel die bewust wel een eigen
// hoogte heeft (aanwezigheid, 420px) het tabelvak. `scrollIntoView` laat de
// browser dat kiezen, en `nearest` doet niets als de rij al in beeld staat.
const scrollRowIntoView = (container, rowKey) => {
  if (!container || !rowKey) return;

  const row = Array.from(container.querySelectorAll('[data-row-key]')).find(
    (element) => element.dataset.rowKey === rowKey
  );

  if (!row || typeof row.scrollIntoView !== 'function') return;

  row.scrollIntoView({ block: 'nearest' });
};

// Hoogte van het vak van een brede tabel: de ruimte onder zijn bovenkant op de
// pagina, zodat de horizontale balk zonder scrollen in beeld staat. Nooit
// kleiner dan WIDE_MIN_HEIGHT, anders valt er niet in te werken. Begint de
// tabel pas onder de vouw, dan scrolt de pagina er eerst heen en mag het vak
// het hele scherm vullen. Een gemeten waarde, geen opmaak: de CSS leest hem als
// variabele (--common-table-wide-height in table.css).
// (Eerder: "onder de helft → hele scherm". Op een scherm van 779 met de tabel
// op 390 stond de balk dan 355px onder de vouw.)
const WIDE_MARGIN = 24;
const WIDE_FULL_SCREEN_MARGIN = 32;
const WIDE_MIN_HEIGHT = 320;

const wideTableHeight = (container) => {
  // De rij '+ toevoegen' staat onder het vak en plakt onderaan het scherm; laat
  // er ruimte voor, anders ligt hij over de balk opzij.
  const addRow = container.parentElement?.querySelector(':scope > .common-table__add-row');
  const screen = window.innerHeight - (addRow ? addRow.offsetHeight : 0);
  const top = container.getBoundingClientRect().top + window.scrollY;
  if (top >= window.innerHeight) return screen - WIDE_FULL_SCREEN_MARGIN;

  return Math.max(screen - top - WIDE_MARGIN, WIDE_MIN_HEIGHT);
};

// Twee lijsten met dezelfde rijen in dezelfde volgorde. Geen diepe
// vergelijking: het filter geeft de objecten uit `items` ongewijzigd door, dus
// een huishouden dat veranderde is per definitie een ander object.
const sameItems = (a, b) => (
  Array.isArray(a)
  && Array.isArray(b)
  && a.length === b.length
  && a.every((item, index) => item === b[index])
);

// asc -> desc -> off. Clearing the sort restores the caller's own order, which
// is also the order the drag handles write back to Firestore.
const nextDirection = (current) => {
  if (current === 'asc') return 'desc';
  if (current === 'desc') return null;
  return 'asc';
};

const getSortValue = (item, column) => {
  if (typeof column.sortAccessor === 'function') return column.sortAccessor(item);
  if (typeof column.accessor === 'function') return column.accessor(item);
  return column.key ? item[column.key] : undefined;
};

const getDefaultSearchValue = (item, columns) => {
  const firstSearchableColumn = columns.find((column) => column.searchable !== false);

  if (!firstSearchableColumn) {
    return '';
  }

  if (typeof firstSearchableColumn.searchAccessor === 'function') {
    return firstSearchableColumn.searchAccessor(item) || '';
  }

  if (typeof firstSearchableColumn.accessor === 'function') {
    return firstSearchableColumn.accessor(item) || '';
  }

  if (firstSearchableColumn.key && item[firstSearchableColumn.key] !== undefined) {
    return item[firstSearchableColumn.key] || '';
  }

  return '';
};

/**
 * De gedeelde tabel: zoeken, sorteren, slepen, lege toestand en een
 * scrollcontainer die een nieuwe rij in beeld schuift.
 *
 * @param {object} props
 * @param {Array<object>} props.columns - Kolomdefinities: `{key, label, searchable, sortable, sortAccessor, sortRank, accessor, headerClassName}`
 * @param {Array<object>} props.items - De rijen, in de volgorde van de aanroeper
 * @param {(item: object, index: number, context: {searchTerm: string}) => React.ReactNode} props.renderRow - Rendert de cellen van één rij
 * @param {(item: object, index: number) => string} [props.getItemKey] - Sleutel per rij
 * @param {(item: object) => string} [props.searchAccessor] - De tekst waarop gezocht wordt; standaard de eerste doorzoekbare kolom
 * @param {string} [props.searchPlaceholder] - Tekst in het lege zoekveld
 * @param {(shown: number, total: number) => string} [props.searchSummary] - Regel naast het zoekveld, alleen zichtbaar tijdens zoeken
 * @param {React.ReactNode} [props.searchBarStart] - Vrij blok in de zoekbalk, direct na het zoekveld (filters die bij de tabel horen)
 * @param {React.ReactNode} [props.searchBarEnd] - Vrij blok rechts in de zoekbalk (bijv. een weergaveschakelaar)
 * @param {() => void} [props.onSearchActive] - Aangeroepen zodra het zoekveld focus krijgt; om gegevens op te halen die alleen voor zoeken nodig zijn
 * @param {(items: Array<object>) => void} [props.onFilteredItemsChange] - Aangeroepen met de rijen die de zoekterm overlaat; voor een export die 'alles op het scherm' belooft
 * @param {boolean} [props.showSearch] - Of het zoekveld getoond wordt
 * @param {string} [props.emptyMessage] - Tekst wanneer er geen rijen zijn
 * @param {{toolTipContent: string, onClick: Function, className?: string}} [props.addRow] - Knop onder de tabel om een rij toe te voegen
 * @param {boolean} [props.draggable] - Of rijen versleepbaar zijn (uit tijdens zoeken en sorteren)
 * @param {string} [props.droppableId] - Id van het sleepgebied
 * @param {(result: object) => void} [props.onDragEnd] - Aangeroepen na een sleepactie
 * @param {string} [props.className] - Extra class op de buitenste wikkel
 * @param {string} [props.outerClassName] - Extra class op de buitenste wikkel
 * @param {string} [props.containerClassName] - Extra class op de scrollcontainer
 * @param {string} [props.tableClassName] - Extra class op het `table`-element
 * @param {Array<Array<object>>} [props.headerRows] - Meerdere koprijen; kolommen hierin kunnen ook sorteren
 * @param {React.ReactNode} [props.footerRow] - Afsluitende rij binnen het raster, buiten zoeken en sorteren
 * @param {(item: object, index: number) => string} [props.getRowClassName] - Extra class per rij
 * @param {(item: object, index: number) => object} [props.rowProps] - Extra props per rij
 * @param {string} [props.ariaLabel] - Naam van de tabel voor schermlezers; nodig zodra er twee tabellen op één pagina staan
 */
const Table = ({
  columns = [],
  items = [],
  renderRow,
  getItemKey = (item, index) => item?.ID || item?.id || `${index}`,
  searchAccessor,
  searchPlaceholder = 'Zoeken op eerste kolom',
  searchSummary,
  searchBarStart,
  searchBarEnd,
  onSearchActive,
  onFilteredItemsChange,
  showSearch = true,
  emptyMessage = 'Geen resultaten gevonden.',
  addRow = null,
  draggable = false,
  droppableId = 'common-table',
  onDragEnd,
  className = '',
  outerClassName = '',
  containerClassName = '',
  tableClassName = '',
  headerRows = null,
  footerRow = null,
  getRowClassName,
  rowProps,
  ariaLabel,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sort, setSort] = useState({ key: null, direction: null });
  // hasRowsBelow: er staan nog rijen ONDER de zichtbare hoogte. Kan alleen in
  // een tabel met een eigen vak (--wide, of een containerClassName met een
  // hoogte); macOS tekent die schuifbalk pas tijdens het scrollen, dus zonder
  // vervaging ziet een lijst van 7 eruit als een complete lijst van 5.
  // isWide: breder dan de kaart, zie syncScrollState.
  // Eén object, zodat een meting nooit twee renders kost.
  const [scrollState, setScrollState] = useState({ hasRowsBelow: false, isWide: false });
  const { hasRowsBelow, isWide } = scrollState;
  const containerRef = useRef(null);
  const previousRowKeysRef = useRef(null);
  const userAddedAtRef = useRef(0);

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();

  const filteredItems = useMemo(() => {
    if (!normalizedSearchTerm) {
      return items;
    }

    return items.filter((item) => {
      const rawValue = searchAccessor
        ? searchAccessor(item)
        : getDefaultSearchValue(item, columns);

      return String(rawValue || '').toLowerCase().includes(normalizedSearchTerm);
    });
  }, [columns, items, normalizedSearchTerm, searchAccessor]);

  // AI-CONTEXT: See docs/pitfalls.md#ui-export-claims-everything-on-screen
  // De zoekterm woont hier, dus de aanroeper kan niet zien wat er overblijft.
  // Een export die 'alles wat op het scherm staat' belooft moet dat wél weten:
  // zonder deze melding schrijft hij alle huishoudens weg terwijl er drie in
  // beeld staan.
  //
  // De callback in een ref, niet in de dependency-array: een inline arrow
  // krijgt elke render een nieuwe identiteit, en dan zou dit effect elke render
  // draaien in plaats van alleen bij een echte wijziging.
  const onFilteredItemsChangeRef = useRef(onFilteredItemsChange);
  onFilteredItemsChangeRef.current = onFilteredItemsChange;
  // Wat er het laatst gemeld is. Zodra er gezocht wordt bouwt `filteredItems`
  // een NIEUWE array — ook als er niets veranderde — en de aanroeper bewaart
  // die doorgaans in state. Zonder deze vergelijking is dat een rondje zonder
  // eind: melden → de ouder rendert → een nieuwe `searchAccessor` → opnieuw
  // filteren → opnieuw melden. Het zoekveld bevroor dan na één letter.
  const reportedItemsRef = useRef(null);

  useEffect(() => {
    if (!onFilteredItemsChangeRef.current) return;
    // Op verwijzing, niet op inhoud: de rijen zijn dezelfde objecten als in
    // `items`, dus een gewijzigd huishouden is een ander object en wordt wél
    // gemeld.
    if (sameItems(reportedItemsRef.current, filteredItems)) return;

    reportedItemsRef.current = filteredItems;
    onFilteredItemsChangeRef.current(filteredItems);
  }, [filteredItems]);

  // Ook de kolommen uit `headerRows` meenemen: TableHeader rendert daar net zo
  // goed een sorteerknop, en zonder deze lookup zet die knop wel de state
  // (pijltje aan, slepen uit) maar sorteert hij niets — een stille no-op.
  const allColumns = useMemo(
    () => (headerRows ? headerRows.flat() : columns),
    [columns, headerRows]
  );

  const sortedColumn = sort.key
    ? allColumns.find((column) => column.key === sort.key)
    : null;

  // Search first, then sort what is left.
  const visibleItems = useMemo(() => {
    if (!sortedColumn || !sort.direction) return filteredItems;

    return sortItems(filteredItems, {
      accessor: (item) => getSortValue(item, sortedColumn),
      direction: sort.direction,
      rank: sortedColumn.sortRank,
    });
  }, [filteredItems, sortedColumn, sort.direction]);

  const handleSort = (columnKey) => {
    setSort((current) => (
      current.key === columnKey
        // Same column: step to the next direction, dropping the key when off.
        ? { key: nextDirection(current.direction) ? columnKey : null, direction: nextDirection(current.direction) }
        // A different column always starts ascending.
        : { key: columnKey, direction: 'asc' }
    ));
  };

  const rowKeys = visibleItems.map((item, index) => String(getItemKey(item, index)));
  // AI-CONTEXT: See docs/pitfalls.md#state-useeffect-inline-filter
  // `rowKeys` is a fresh array every render, so the effect keys off its
  // serialised form instead of the array identity.
  const rowKeysSignature = rowKeys.join('|');

  useEffect(() => {
    const previousRowKeys = previousRowKeysRef.current;
    previousRowKeysRef.current = rowKeys;

    const newRowKey = findNewRowKey(previousRowKeys, rowKeys);
    if (!newRowKey || Date.now() - userAddedAtRef.current > USER_ADD_WINDOW_MS) return;

    userAddedAtRef.current = 0;
    scrollRowIntoView(containerRef.current, newRowKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowKeysSignature]);

  const handleAddRowClick = (...args) => {
    userAddedAtRef.current = Date.now();
    return addRow.onClick(...args);
  };

  const syncScrollState = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const remaining = container.scrollHeight - container.clientHeight - container.scrollTop;
    const rowsBelow = remaining > 1;
    // Breder dan de kaart: dan wordt het tabelvak zo hoog als het scherm
    // (CSS: --wide), zodat de horizontale balk in beeld staat in plaats van
    // onderaan een lange tabel. Zie docs/features/scrollbalken-opruimen.md.
    const wide = container.scrollWidth - container.clientWidth > 1;
    if (wide) container.style.setProperty('--common-table-wide-height', `${wideTableHeight(container)}px`);
    setScrollState((previous) => (
      previous.hasRowsBelow === rowsBelow && previous.isWide === wide
        ? previous
        : { hasRowsBelow: rowsBelow, isWide: wide }
    ));
  }, []);

  // Layout-effect: een passende tabel is geen scrollvak (overflow: visible), dus
  // een brede tabel steekt buiten zijn kaart tot hij --wide krijgt. Meten vóór
  // het tekenen voorkomt dat dat één beeld lang te zien is.
  useLayoutEffect(() => {
    syncScrollState();

    const container = containerRef.current;
    if (!container) return undefined;

    container.addEventListener('scroll', syncScrollState);
    // Een hoger of lager venster verandert de hoogte van het vak, niet zijn breedte.
    window.addEventListener('resize', syncScrollState);
    // ResizeObserver bestaat niet in jsdom; zonder is de vervaging simpelweg uit.
    // De tabel zelf ook volgen: hij kan breder worden terwijl zijn vak gelijk
    // blijft, en hij wordt hoger als er rijen bijkomen.
    // Een beeld later meten: --wide aan- of uitzetten verandert de grootte van
    // het vak dat de observer net meldde, en binnen de callback geeft dat de
    // melding "ResizeObserver loop completed with undelivered notifications".
    let frame = 0;
    const scheduleSync = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(syncScrollState);
    };
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(scheduleSync) : null;
    if (observer) {
      observer.observe(container);
      if (container.firstElementChild) observer.observe(container.firstElementChild);
    }

    return () => {
      container.removeEventListener('scroll', syncScrollState);
      window.removeEventListener('resize', syncScrollState);
      cancelAnimationFrame(frame);
      if (observer) observer.disconnect();
    };
  }, [syncScrollState]);

  // Rijen komen meestal pas na het aanhaken binnen (Firestore). Ook dan meten
  // vóór het tekenen, anders steekt een tabel die daardoor breed wordt één
  // beeld lang buiten zijn kaart; de observer hierboven zou een beeld later zijn.
  useLayoutEffect(() => {
    syncScrollState();
  }, [syncScrollState, rowKeysSignature]);

  const classes = ['table-outer-container', 'common-table-shell', className, outerClassName]
    .filter(Boolean)
    .join(' ');
  // Dropping a row rewrites Position in Firestore. While the view is sorted or
  // filtered, that would persist the order the user is looking at instead of
  // the one they built by hand — so dragging is off until both are cleared.
  const resolvedDraggable = draggable && !normalizedSearchTerm && !sort.direction;

  // Een sleepgreep die er onveranderd bij staat terwijl slepen uit is, nodigt
  // uit tot een sleep die niets doet. Geldt ook tijdens zoeken, niet alleen
  // tijdens sorteren.
  const dragSuppressed = draggable && !resolvedDraggable;

  // Wat een rij nodig heeft maar niet uit het item zelf kan halen. Vandaag
  // alleen de zoekterm: een rij die matcht op een kolom die niet op het scherm
  // staat, moet eronder kunnen uitleggen waaróm hij er staat.
  // Getrimd, zodat de rij op dezelfde term kijkt als waarop gefilterd is.
  const rowContext = useMemo(
    () => ({ searchTerm: searchTerm.trim() }),
    [searchTerm]
  );

  return (
    <div className={classes}>
      {/* Ook zonder zoekveld: wie alleen `searchBarEnd` meegeeft wil die balk
          toch hebben, anders verdwijnt zijn blok zonder melding. */}
      {showSearch || searchBarStart || searchBarEnd ? (
        <div className="common-table__search-bar">
          {showSearch ? (
            <TableSearch
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder={searchPlaceholder}
              onFocus={onSearchActive}
            />
          ) : null}
          {/* Hoe hard er gefilterd is. Alleen tijdens zoeken: zonder zoekterm
              telt de regel iets op wat de gebruiker gewoon ziet staan. */}
          {searchSummary && normalizedSearchTerm ? (
            <p className="common-table__search-summary">
              {searchSummary(visibleItems.length, items.length)}
            </p>
          ) : null}
          {/* Filters op dezelfde regel als het zoekveld, achter een dunne
              scheidslijn (CSS). */}
          {searchBarStart ? (
            <div className="common-table__search-start">{searchBarStart}</div>
          ) : null}
          {searchBarEnd ? (
            <div className="common-table__search-end">{searchBarEnd}</div>
          ) : null}
        </div>
      ) : null}
      <TableContainer
        className={[containerClassName, isWide ? 'common-table__container--wide' : ''].filter(Boolean).join(' ')}
        innerRef={containerRef}
      >
        <table
          aria-label={ariaLabel}
          className={[
            'common-table',
            // Haakt de sleepgrepen uit in CSS.
            dragSuppressed ? 'common-table--drag-off' : '',
            tableClassName,
          ].filter(Boolean).join(' ')}
        >
          <TableHeader
            columns={columns}
            headerRows={headerRows}
            sort={sort}
            onSort={handleSort}
          />
          <TableBody
            items={visibleItems}
            renderRow={renderRow}
            getItemKey={getItemKey}
            columnsCount={columns.length}
            emptyMessage={emptyMessage}
            draggable={resolvedDraggable}
            droppableId={droppableId}
            onDragEnd={onDragEnd}
            getRowClassName={getRowClassName}
            rowProps={rowProps}
            rowContext={rowContext}
          />
          {/* Optionele afsluitregel onder de tabel: kolomtotalen horen in het
              raster zelf, want los eronder lopen ze niet meer in de pas met de
              kolommen. Blijft buiten zoeken en sorteren — hij is geen rij. */}
          {footerRow ? (
            <tfoot className="common-table__foot">
              <tr className="common-table__foot-row">{footerRow}</tr>
            </tfoot>
          ) : null}
        </table>
        {hasRowsBelow ? <div className="common-table__more-shade" aria-hidden="true" /> : null}
      </TableContainer>
      {addRow ? (
        <TableAddRow
          toolTipContent={addRow.toolTipContent}
          onClick={handleAddRowClick}
          className={addRow.className}
        />
      ) : null}
    </div>
  );
};

export default Table;
